// UI helper module for the live-progress line on a Test Run card.
//
// The QA chat's Test Run card only moved when a journey reached a terminal state, so a healthy run
// whose current journey took minutes rendered a byte-identical collapsed card for the whole stretch —
// which a user reads as a dead end (bug bug-mu2p97vx-7abw: "In Progress 0/4 journeys" never changed
// while the run was alive, and its eventual failure was never surfaced). The data to fix that is
// already in the polled test-sessions payload; these helpers turn it into one line of text: what is
// running right now, how much is queued behind it, and how long the round has been going.

/** Seconds a round has been running, or null when the timestamp is missing, malformed, or in the future. */
export function elapsedSeconds(startedIso: string | null | undefined, nowMs: number): number | null {
  if (!startedIso) return null
  const started = new Date(startedIso).getTime()
  if (Number.isNaN(started) || nowMs < started) return null
  return Math.floor((nowMs - started) / 1000)
}

/** Compact human duration for a live card: "42s", "2m 07s", "1h 03m". */
export function formatElapsed(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const seconds = safe % 60
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`
  return `${seconds}s`
}

/**
 * The collapsed card's live line, or null when there is nothing in flight to name. With journeys
 * running it names the current one (and any excess from parallel runs); with only a queue it says
 * what is waiting. The elapsed time is appended whenever known, so the line visibly ticks.
 */
export function liveRunLine(input: {
  runningLabels: string[]
  queuedCount: number
  elapsedSeconds: number | null
}): string | null {
  const parts: string[] = []
  if (input.runningLabels.length > 0) {
    const more = input.runningLabels.length > 1 ? ` +${input.runningLabels.length - 1} more` : ''
    parts.push(`Running: ${input.runningLabels[0]}${more}`)
    if (input.queuedCount > 0) parts.push(`${input.queuedCount} queued`)
  } else if (input.queuedCount > 0) {
    parts.push(`Next up: ${input.queuedCount} queued`)
  } else {
    return null
  }
  if (input.elapsedSeconds != null) parts.push(formatElapsed(input.elapsedSeconds))
  return parts.join(' · ')
}
